import isApp from "shared/util/isApp";
import Analytics from "analytics/util/Analytics";
import { validate, createErrorMessage } from "apps/signup/util/helper";
import { FIELDS, REGISTRATION_TYPES } from "apps/signup/util/constants";
import Api from "apps/signup/util/web";
import { confirmToken } from "ducks/recaptcha";
import { trackOnboardingRegistered } from "analytics/signup";

// ////////////////////////
// ACTION TYPES //////////
// //////////////////////

export const UPDATE_FIELD = "signup.UPDATE_FIELD";
export const VALIDATE_FIELD = "signup.VALIDATE_FIELD";
export const VALIDATE_FIELD_SUCCESS = "signup.VALIDATE_FIELD_SUCCESS";
export const VALIDATE_FIELD_FAIL = "signup.VALIDATE_FIELD_FAIL";
export const DISMISS_SUGGESTION = "signup.DISMISS_SUGGESTION";
export const REDIRECT_TO_ONBOARDING = "signup.REDIRECT_TO_ONBOARDING";

export const SUBMIT_API = "signup.SUBMIT_API";
export const SUBMIT_API_SUCCESS = "signup.SUBMIT_API_SUCCESS";
export const SUBMIT_API_FAIL = "signup.SUBMIT_API_FAIL";
export const REGISTER_SUCCESS = "signup.REGISTER_SUCCESS";

export const MAYBE_SHOW_CAPTCHA = "signup.MAYBE_SHOW_CAPTCHA";
export const HIDE_CAPTCHA = "signup.HIDE_CAPTCHA";
export const SHOW_CAPTCHA = "signup.SHOW_CAPTCHA";
export const STORE_CAPTCHA = "signup.STORE_CAPTCHA";
export const CONFIRM_RECAPTCHA_SUCCESS = "signup.CONFIRM_RECAPTCHA_SUCCESS";
export const SET_RECAPTCHA_V3_TOKEN = "signup.SET_RECAPTCHA_V3_TOKEN";
export const SET_CURRENT_FIELD = "signup.SET_CURRENT_FIELD";

// ////////////////////////
// INITIAL STATE /////////
// //////////////////////

export const INITIAL_STATE = {
	email: "",
	password: "",
	captcha: "",
	captchaV3: "",

	feedback: {},
	errors: {},
	suggestions: {},
	success: {},

	isValidating: false,
	isUpdating: false,
	seenSuggestion: false,
	isSubmitting: false,
	isSuccess: false,

	showCaptcha: false,
	hasCheckedCaptcha: false,

	currentField: FIELDS.EMAIL,

	userid: null,
};


// ////////////////////////
// REDUCER ///////////////
// //////////////////////

export function reducer(state = INITIAL_STATE, action) {
	switch (action.type) {
		case UPDATE_FIELD: {
			let {
				errors, feedback, suggestions,
			} = state;

			errors = {
				...errors,
				[action.field]: false,
			};

			feedback = {
				...feedback,
				[action.field]: "",
			};

			suggestions = {
				...suggestions,
				[action.field]: false,
			};

			// clear out registration error if the user updates the password input
			if (action.field === "password") {
				errors.registration = false;
				feedback.registration = "";
			}

			return {
				...state,
				[action.field]: action.data,
				errors,
				feedback,
				suggestions,
				isUpdating: true,
			};
		}

		case DISMISS_SUGGESTION: {
			return {
				...state,
				seenSuggestion: true,
				isUpdating: false,
			};
		}

		case VALIDATE_FIELD: {
			// reset field properties on every validation
			let {
				errors, feedback, suggestions, success,
			} = state;

			errors = {
				...errors,
				[action.field]: false,
			};

			success = {
				...success,
				[action.field]: false,
			};

			feedback = {
				...feedback,
				[action.field]: "",
			};

			suggestions = {
				...suggestions,
				[action.field]: "",
			};

			return {
				...state,
				errors,
				success,
				feedback,
				suggestions,
				isValidating: true,
				isUpdating: false,
			};
		}

		case VALIDATE_FIELD_SUCCESS: {
			let {
				errors, feedback, suggestions, success,
			} = state;

			errors = {
				...errors,
				[action.field]: false,
			};

			feedback = {
				...feedback,
				[action.field]: action.feedback,
			};

			suggestions = {
				...suggestions,
				[action.field]: action.suggestion,
			};

			success = {
				...success,
				[action.field]: true,
			};

			return {
				...state,
				errors,
				success,
				feedback,
				suggestions,
				isValidating: false,
				seenSuggestion: false,
			};
		}

		case VALIDATE_FIELD_FAIL: {
			let {
				errors, suggestions, feedback, success,
			} = state;

			errors = {
				...errors,
				[action.field]: true,
			};

			feedback = {
				...feedback,
				[action.field]: action.feedback,
			};

			suggestions = {
				...suggestions,
				[action.field]: action.suggestions,
			};

			success = {
				...success,
				[action.field]: false,
			};

			return {
				...state,
				errors,
				success,
				suggestions,
				feedback,
				isValidating: false,
				seenSuggestion: false,
			};
		}

		case SHOW_CAPTCHA: {
			let { errors, feedback } = state;

			errors = {
				...errors,
				captcha: true,
			};

			feedback = {
				...feedback,
				email: "", // clear email feedback
			};

			return {
				...state,
				isSubmitting: false,
				showCaptcha: true,
				errors,
				feedback,
			};
		}

		case MAYBE_SHOW_CAPTCHA: {
			return {
				...state,
				isSubmitting: true,
				hasCheckedCaptcha: true,
			};
		}

		case HIDE_CAPTCHA: {
			return {
				...state,
				isSubmitting: false,
				showCaptcha: false,
			};
		}

		case STORE_CAPTCHA: {
			let { errors } = state;

			errors = {
				...errors,
				captcha: false,
			};

			return {
				...state,
				isSubmitting: false,
				errors,
				captcha: action.value,
			};
		}

		case SUBMIT_API: {
			return {
				...state,
				isSubmitting: true,
			};
		}

		case SUBMIT_API_SUCCESS: {
			return {
				...state,
				isSuccess: true,
				isSubmitting: false,
			};
		}

		case REGISTER_SUCCESS: {
			return {
				...state,
				isSubmitting: false,
				userid: action.payload.userid,
			};
		}

		case SUBMIT_API_FAIL: {
			let { errors, feedback } = state;

			errors = {
				...errors,
				[action.payload.field]: true,
			};

			feedback = {
				...feedback,
				[action.payload.field]: action.payload.message,
			};

			return {
				...state,
				isSubmitting: false,
				errors,
				feedback,
			};
		}

		case SET_CURRENT_FIELD: {
			return {
				...state,
				currentField: action.field,
			};
		}

		case REDIRECT_TO_ONBOARDING: {
			return state;
		}

		case SET_RECAPTCHA_V3_TOKEN: {
			const { payload: { token } } = action;

			return {
				...state,
				captchaV3: token,
			};
		}

		default: {
			return state;
		}
	}
}


// ////////////////////////
// ACTION CREATORS ///////
// //////////////////////

/**
 * Register a new user to mParticle and subsequently fire mParticle login event.
 * @param {string} userid - User ID for fresh user.
 * @param {string} field - Field used to register (Facebook or normal email/password).
 * @returns {Promise} Promise that resolves when UI events for registering have fired.
 */
function _logSuccessfulRegister({ userid, field }) {
	// Synchronous function, but puts all UI events
	// behind a queue to fire only when this has finished.
	Analytics.logInIfNeeded(userid);

	if (field === FIELDS.REGISTRATION) {
		trackOnboardingRegistered(REGISTRATION_TYPES.PASSWORD);
		Analytics.logUIEvent(Analytics.eventNames.onboardingSelectedJoinOnRegistration2);
	} else if (field === FIELDS.FACEBOOK) {
		trackOnboardingRegistered(REGISTRATION_TYPES.FACEBOOK);
	}

	return Promise.resolve(); // promise noop.
}

function _redirectToOnboarding(step) {
	const destination = `/onboarding${step ? `?step=${step}` : ""}`;

	if (isApp()) {
		window.App.loadPage(destination, {
			post_login: 1,
			clear_history: 1,
		});
	}
	else {
		window.location.href = destination;
	}
}

export function updateField(field, data) {
	return {
		type: UPDATE_FIELD,
		field,
		data,
	};
}

export function dismissSuggestion() {
	return { type: DISMISS_SUGGESTION };
}

export function storeCaptcha(value) {
	return {
		type: STORE_CAPTCHA,
		value,
	};
}

export function setCaptchaV3Token(token) {
	return {
		type: SET_RECAPTCHA_V3_TOKEN,
		payload: {
			token,
		},
	};
}

export function setCurrentField(field) {
	const currentField = field || FIELDS.EMAIL;

	return {
		type: SET_CURRENT_FIELD,
		field: currentField,
	};
}

export function redirectToOnboarding() {
	_redirectToOnboarding();

	return { type: REDIRECT_TO_ONBOARDING };
}

function confirmCaptchaTokenForPassword(token, isCaptchaShowing) {
	return confirmToken({
		type: CONFIRM_RECAPTCHA_SUCCESS,
		token,
		isCaptchaShowing,
		page: "password",
	});
}


export function maybeShowCaptcha(callbackFunction) {
	return (dispatch, getStore) => {
		dispatch({ type: MAYBE_SHOW_CAPTCHA });

		const store = getStore().signup;

		Api.getShouldShowCaptcha()
			.then((res) => {
				if (res && res.show_captcha === 0) {
					dispatch({ type: HIDE_CAPTCHA });
					dispatch(confirmCaptchaTokenForPassword(store.captchaV3, false));
					return dispatch(callbackFunction());
				} else {
					dispatch(confirmCaptchaTokenForPassword(store.captchaV3, true));
					return dispatch({ type: SHOW_CAPTCHA });
				}
			})
			.catch(() => {
				dispatch(confirmCaptchaTokenForPassword(store.captchaV3, true));
				return dispatch({ type: SHOW_CAPTCHA });
			});
	};
}

export function submitRegistration() {
	return (dispatch, getStore) => {
		const store = getStore().signup;

		// first check if they need captcha;
		if (!store.hasCheckedCaptcha) {
			dispatch(maybeShowCaptcha(submitRegistration));
			return;
		}

		dispatch({ type: SUBMIT_API, field: FIELDS.EMAIL });

		Api.submitRegistration({
			email: store.email,
			password: store.password,
			captcha: store.captcha,
		})
			.then((res) => {
				if (res.user && res.user.userid && res.user.authcode) {
					dispatch({
						type: SUBMIT_API_SUCCESS,
						field: FIELDS.REGISTRATION,
						userid: res.user.userid,
					});
					_logSuccessfulRegister({
						userid: res.user.userid,
						field: FIELDS.REGISTRATION,
					});

					_redirectToOnboarding();
				} else {
					const message = createErrorMessage(res.reasons);

					dispatch({
						type: SUBMIT_API_FAIL,
						payload: {
							field: FIELDS.REGISTRATION,
							message,
						},
						error: true,
					});
				}
			})
			.catch(() => {
				dispatch({
					type: SUBMIT_API_FAIL,
					payload: {
						field: FIELDS.REGISTRATION,
					},
					error: true,
				});
			});
	};
}

export function validateField(field, value) {
	return (dispatch, getStore) => {
		dispatch({
			type: VALIDATE_FIELD,
			field,
		});

		const { seenSuggestion } = getStore().signup;
		const params = {
			field,
			value,
			seenSuggestion,

			// success func
			pass: (success, suggestion) => {
				dispatch({
					type: VALIDATE_FIELD_SUCCESS,
					field,
					suggestion,
					feedback: success,
				});
			},

			// error func
			fail: (error) => {
				dispatch({
					type: VALIDATE_FIELD_FAIL,
					field,
					feedback: error,
				});
			},

			// suggestion
			suggest: (error, suggestions) => {
				dispatch({
					type: VALIDATE_FIELD_FAIL,
					field,
					feedback: error,
					suggestions,
				});
			},
		};


		return validate(params);
	};
}



// WEBPACK FOOTER //
// ./src/ducks/signup.js